// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type PartAttributeDefinitionId,
  type PartAttributeDefinitionVariantId,
  type PartAttributeDefinition,
  useEvolvePaginatedGet,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Button, Checkbox, Flex, Stack, Text, InputLabel } from '@mantine/core';
import { useForm } from '@mantine/form';

import { InfoIcon } from 'components/Mantine/InfoIcon';
import { MultiOptionCreator } from 'components/Mantine/MultiOptionCreator';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { WrappedMultiSelect } from 'components/Mantine/TypeSafeSelect/WrappedMultiSelect';
import { isNotNil, isNil } from 'helpers/isNotNil';

type ModeProps =
  | {
      onCustomAttributeCreated: (attribute: { name: string; allowWriteInVariant: boolean; variants: string[] }) => void;
      attributesSelected: PartAttributeDefinitionId[];
      onAttributeSelected: (
        attribute: {
          partAttributeDefinitionId: PartAttributeDefinitionId;
          partAttributeDefinitionVariantIds: PartAttributeDefinitionVariantId[];
        },
        globalAttribute: PartAttributeDefinition,
      ) => void;
      globalCreator?: false;
    }
  | {
      /** If true, we are creating a new Global attribute, not editing an existing Part */
      globalCreator: true;
      onGlobalAttributeCreated: (attribute: PartAttributeDefinition) => void;
    };

type Props = {
  disabled?: boolean;
  label?: string;
} & ModeProps;

type FormValues = {
  selectedAttribute: PartAttributeDefinition | null;
  selectedVariants: PartAttributeDefinitionVariantId[];
  creatingAttribute: string | null;
  creatingVariants: string[];
  writeInAllowed: boolean;
  addGlobally: boolean;
};

const selectionValid = (values: FormValues) => isNotNil(values.selectedAttribute) && values.selectedVariants.length > 0;
const creationValid = (values: FormValues) => !!values.creatingAttribute && values.creatingVariants.length > 0;

const AddAttributeFields = (props: ModeProps) => {
  const paginatedGetGlobalAttributes = useEvolvePaginatedGet('shop/partCatalog/globalAttribute');
  const { apiCall: addGlobalAttribute } = useEvolvePost('shop/partCatalog/globalAttribute');

  const form = useForm<FormValues>({
    initialValues: {
      selectedAttribute: null,
      selectedVariants: [],
      creatingAttribute: props.globalCreator ? '' : null,
      creatingVariants: [],
      writeInAllowed: false,
      addGlobally: props.globalCreator ?? false,
    },
    validate: {
      selectedAttribute: (_, values) => !(selectionValid(values) || creationValid(values)),
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    const { creatingAttribute, creatingVariants, writeInAllowed, addGlobally } = values;
    let existingAttribute = values.selectedAttribute;
    let attributeVariants = values.selectedVariants;
    if (creatingAttribute) {
      if (addGlobally || props.globalCreator) {
        existingAttribute = await addGlobalAttribute({
          name: creatingAttribute,
          allowWriteInVariant: writeInAllowed,
          variants: creatingVariants,
        });
        attributeVariants = existingAttribute.variants.map((v) => v.partAttributeDefinitionVariantId);
        if (props.globalCreator) {
          props.onGlobalAttributeCreated(existingAttribute);
        }
      } else {
        props.onCustomAttributeCreated({
          name: creatingAttribute,
          variants: creatingVariants,
          allowWriteInVariant: writeInAllowed,
        });
      }
    }
    if (isNotNil(existingAttribute) && !props.globalCreator) {
      props.onAttributeSelected(
        {
          partAttributeDefinitionId: existingAttribute.partAttributeDefinitionId,
          partAttributeDefinitionVariantIds: attributeVariants,
        },
        existingAttribute,
      );
    }
  };

  useFormPopover(form, onSubmit);

  const { selectedAttribute, creatingAttribute } = form.values;

  return (
    <Stack>
      {!props.globalCreator && !selectedAttribute && isNil(creatingAttribute) && (
        <SearchableSelect<'shop/partCatalog/globalAttribute', PartAttributeDefinition, 'partAttributeDefinitionId'>
          label="Attribute Name"
          required
          idKey="partAttributeDefinitionId"
          paginatedGet={paginatedGetGlobalAttributes}
          onChange={(v) => form.setFieldValue('selectedAttribute', v)}
          searchKey="name"
          useLike={false}
          creatable
          createLabel="Create new attribute"
          autoFocus
          data-autofocus
          createdValue={creatingAttribute}
          onCreate={(v) => form.setFieldValue('creatingAttribute', v)}
          modalBehavior="no-modal"
          getItemLabel={(o) => o.name}
          excludeOption={(o) => props.attributesSelected.includes(o.partAttributeDefinitionId)}
          clearable
          maxLength={64}
        />
      )}

      {isNotNil(creatingAttribute) && (
        <>
          <TextInputWrapped
            label="Attribute Name"
            required
            autoFocus={!creatingAttribute}
            data-autofocus={!creatingAttribute}
            onChange={(v) => form.setFieldValue('creatingAttribute', v)}
            value={creatingAttribute}
            maxLength={64}
          />
          <MultiOptionCreator
            placeholder="Add variants..."
            value={form.values.creatingVariants}
            autoFocus={!!creatingAttribute}
            onChange={(v) => form.setFieldValue('creatingVariants', v)}
            required
          />
          <Checkbox
            label={<Text>Allow write-in attribute variants</Text>}
            {...form.getInputProps('writeInAllowed', { type: 'checkbox' })}
          />
          {!props.globalCreator && (
            <Checkbox
              label={
                <Flex align="center" gap={6}>
                  Add to global attributes
                  <InfoIcon label="If checked, this attribute will be added to the list of global attributes so it can be added to other items." />
                </Flex>
              }
              {...form.getInputProps('addGlobally', { type: 'checkbox' })}
            />
          )}
        </>
      )}

      {selectedAttribute && (
        <>
          <Stack gap={0}>
            <InputLabel>Attribute Name</InputLabel>
            <Text>{selectedAttribute.name}</Text>
          </Stack>
          <WrappedMultiSelect
            label="Select Valid Variants"
            placeholder="Select at least one"
            required
            autoFocus
            data-autofocus
            value={form.values.selectedVariants}
            onChange={(v) => form.setFieldValue('selectedVariants', v)}
            description={selectedAttribute.allowWriteInVariant ? 'Write-in values allowed' : undefined}
            data={selectedAttribute.variants.map((v) => ({
              label: v.name,
              value: v.partAttributeDefinitionVariantId,
            }))}
          />
        </>
      )}
    </Stack>
  );
};

export const AddAttributePopover = ({ disabled, label = 'Add attribute', ...props }: Props) => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <FormPopover opened={opened} onClose={() => setOpened(false)} confirmationText="Add" position="top">
        <AddAttributeFields {...props} />
      </FormPopover>
      <Button onClick={() => setOpened((o) => !o)} disabled={disabled} size="compact-sm" variant="light">
        {label}
      </Button>
    </>
  );
};
